import { Download, XIcon } from "@/components/icons";
import { Button } from "@/components/kit";
import { useEffect, useState } from "react";

const DISMISS_KEY = "pwa-install-dismissed";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function isIosSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !(window as Window & { MSStream?: unknown }).MSStream;
}

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (sessionStorage.getItem(DISMISS_KEY) === "1") return;

    function onBeforeInstallPrompt(event: Event) {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
      setVisible(true);
    }

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);

    if (isIosSafari()) {
      setShowIosHint(true);
      setVisible(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    };
  }, []);

  function dismiss() {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
    setDeferredPrompt(null);
    setShowIosHint(false);
  }

  async function handleInstall() {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    dismiss();
  }

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-(--mobile-bottom-nav,0px) z-50 border-secondary/20 border-t bg-card/95 p-4 shadow-lg backdrop-blur-sm sm:bottom-4 sm:mx-auto sm:max-w-md sm:rounded-2xl sm:border">
      <div className="flex items-start gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/20 text-secondary">
          <Download className="size-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm">ເພີ່ມໄປໜ້າຈໍຫຼັກ</p>
          <p className="mt-0.5 text-muted-foreground text-xs leading-relaxed">
            {showIosHint && !deferredPrompt
              ? "ແຕະ Share → Add to Home Screen ເພື່ອເປີດແອັບໄວຂຶ້ນ"
              : "ຕິດຕັ້ງ Sunlower Admin ເພື່ອເຂົ້າໃຊ້ໄດ້ຄືແອັບ"}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {deferredPrompt ? (
              <Button
                size="sm"
                className="rounded-full"
                onClick={handleInstall}
              >
                ຕິດຕັ້ງ
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="outline"
              className="rounded-full"
              onClick={dismiss}
            >
              ເທື່ອນີ້ບໍ່
            </Button>
          </div>
        </div>
        <button
          type="button"
          className="shrink-0 rounded-full p-1 text-muted-foreground hover:bg-muted"
          onClick={dismiss}
          aria-label="ປິດ"
        >
          <XIcon className="size-4" />
        </button>
      </div>
    </div>
  );
}
